import React, { useMemo } from 'react'
import PageLink from './PageLink'
import { getPaginationItems } from '../../utils/getPaginationItems'
import type { PaginationProps } from './Pagination.types'
import createClassName from '../../utils/createClassName'
import Icon from '../Icon'
import Flex from '../Flex'
import { isEmpty } from '../../utils'

const Pagination: React.FC<PaginationProps> = ({
  current = 1,
  total = 1,
  onChange,
  className,
  ...restProps
}) => {
  const classes = createClassName(
    'RnPagination',
    {
      root: true,
    },
    className,
  )

  const generatedTotal = useMemo(() => (isEmpty(total) ? 1 : total), [total])

  const pageNums = getPaginationItems(current, generatedTotal)

  const isPrevDisabled = useMemo(() => current === 1, [current])
  const isNextDisabled = useMemo(() => current === generatedTotal, [current, generatedTotal])

  const onPageClick = (pageNum: number, idx: number) => {
    if (isNaN(pageNum)) {
      if (idx < 3) {
        onChange(pageNums[idx + 2] - 2)
      }
      if (idx > 5) {
        onChange(pageNums[idx - 2] + 2)
      }
      return
    }

    onChange(pageNum)
    return
  }

  return (
    <Flex as='nav' flexWrap='wrap' columnGap='8px' className={classes} {...restProps}>
      <PageLink
        disabled={isPrevDisabled}
        onClick={() => !isPrevDisabled && onChange(current - 1)}
        aria-label='pagination-prev-button'
      >
        <Icon size={24} name='Left-outline' color={isPrevDisabled ? 'black10' : 'blue50'} />
      </PageLink>
      {pageNums.map((pageNum, idx) => (
        <PageLink
          key={`$pagelink-${idx}`}
          active={current === pageNum}
          pageNum={pageNum}
          onClick={() => onPageClick(pageNum, idx)}
        >
          {!isNaN(pageNum) ? pageNum : '...'}
        </PageLink>
      ))}
      <PageLink
        disabled={isNextDisabled}
        onClick={() => !isNextDisabled && onChange(current + 1)}
        aria-label='pagination-next-button'
      >
        <Icon size={24} name='Right-outline' color={isNextDisabled ? 'black10' : 'blue50'} />
      </PageLink>
    </Flex>
  )
}

export default Pagination
